import React from 'react';
import { PageView } from '../types';
import { 
  ShieldCheck, 
  ArrowRight, 
  CheckCircle2, 
  Lock, 
  Bell, 
  GitCommit, 
  Users, 
  Smartphone, 
  ChevronDown, 
  AlertTriangle, 
  Server, 
  Layers
} from 'lucide-react';

interface OpsLandingPageProps {
  setActivePage?: (page: PageView) => void;
  onOpenLeadModal: () => void;
}

export const OpsLandingPage: React.FC<OpsLandingPageProps> = ({ onOpenLeadModal }) => {
  const scrollToClustering = (e: React.MouseEvent) => {
    e.preventDefault();
    const el = document.getElementById('clustering-visualizer');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="ops-landing-page">
      {/* 1. ABOVE THE FOLD (HERO) */}
      <section className="landing-hero-section">
        <div className="section-container">
          <div className="hero-content-center">
            <h1 className="hero-title">
              One hundred alerts. One cause.
            </h1>

            <p className="hero-subtitle">
              When a neighborhood drops, MUSE Telemetry correlates failures across your mixed-vendor network into a single root-cause alert, giving your NOC one screen instead of three browser tabs mid-outage.
            </p>

            <div className="cta-wrapper">
              <button onClick={onOpenLeadModal} className="btn btn-primary btn-hero-lg">
                <span>Book Your 15-Minute Outage Review</span>
                <ArrowRight size={16} />
              </button>

              <p className="cta-microcopy">
                15 minutes: bring your last big outage and we'll show you what it would have looked like on one screen.
              </p>
            </div>

            <div className="credibility-microline">
              <ShieldCheck size={14} className="text-muted-icon" />
              <span>Built for small operators running mixed-vendor networks, by a team that has managed network operations for 30+ years.</span>
            </div>

            <div className="hero-anchor-link">
              <a href="#clustering-visualizer" onClick={scrollToClustering} className="anchor-subtle">
                <span>See how event clustering works</span>
                <ChevronDown size={14} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 1a. WORKFLOW TRUST STRIP (Top 1/3) */}
      <section className="trust-strip-section">
        <div className="section-container">
          <div className="trust-banner-card">
            <div className="trust-icon-badge green-badge">
              <Lock size={18} />
            </div>
            <div className="trust-banner-body">
              <h3>No Tool Displacement: Complements Your Existing Workflow</h3>
              <p>
                MUSE sits behind your firewall using an outbound-only collector (zero open inbound ports) and feeds alerts directly into your existing NOC tools. 
                It enhances your current operation without forcing a risky rip-and-replace of hardware or workflows.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 2. THE VILLAIN: THE OUTAGE DAY CHAOS */}
      <section className="section-padding border-top">
        <div className="section-container">
          <div className="flat-card villain-story-card">
            <div className="villain-badge">
              <AlertTriangle size={18} className="text-red" />
              <span>THE 3:00 AM OUTAGE NIGHTMARE</span>
            </div>

            <h2>When the Neighborhood Drops and the Phones Melt Down</h2>

            <div className="story-body">
              <p>
                A fiber link drops or an optical line terminal card resets. Within seconds, eighty separate alerts flood your NOC. Three vendor dashboards (Calix, Nokia, and your core router monitoring) are open across four monitors. The support phone queue is backing up, and your technicians are scrambling.
              </p>
              <p className="highlight-question">
                Is this eighty individual customer problems, or one shared upstream failure?
              </p>
              <p>
                Without topology correlation, you're forced to guess. You roll two trucks to separate customer sites while the real issue remains hidden at the splitter or node. Wasted truck rolls, exhausted techs, and minutes ticking away on your outage timer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. CORE MECHANIC: EVENT CLUSTERING (THE VISUAL STAR) */}
      <section id="clustering-visualizer" className="section-padding border-top">
        <div className="section-container">
          <div className="section-heading text-center">
            <h2>Stop Chasing Eighty Alerts. Fix the One Root Cause.</h2>
            <p>MUSE Telemetry automatically correlates simultaneous failures by physical topology and geolocation, suppressing downstream noise.</p>
          </div>

          {/* SIDE BY SIDE BEFORE VS AFTER */}
          <div className="clustering-split-grid">
            {/* LEFT: Raw Alert Flood */}
            <div className="cluster-column noise-col">
              <div className="col-header">
                <span className="status-dot red" />
                <span className="col-tag font-mono">WITHOUT MUSE: RAW ALERT FLOOD</span>
              </div>
              <div className="terminal-box">
                <div className="term-line red-txt">[CRITICAL] 03:14:02 - OLT-NORTH-02: Port 4 Link Down</div>
                <div className="term-line red-txt">[CRITICAL] 03:14:02 - ONT-89421: Signal Loss</div>
                <div className="term-line amber-txt">[WARNING]  03:14:03 - Node-B-Splitter-3: Unreachable</div>
                <div className="term-line red-txt">[CRITICAL] 03:14:03 - ONT-89425: Power Loss / Dying Gasp</div>
                <div className="term-line red-txt">[CRITICAL] 03:14:04 - ONT-89430: Signal Loss</div>
                <div className="term-line amber-txt">[WARNING]  03:14:05 - Core-Agg-01: BFD Session Degraded</div>
                <div className="term-line red-txt">[CRITICAL] 03:14:05 - ONT-89441: Signal Loss</div>
                <div className="term-line red-txt">[CRITICAL] 03:14:06 - ONT-89452: Unreachable</div>
                <div className="term-line dim-txt">... [Dozens of additional individual alerts flooding NOC]</div>
              </div>
            </div>

            {/* RIGHT: Clustered Root Cause */}
            <div className="cluster-column rootcause-col">
              <div className="col-header">
                <span className="status-dot green" />
                <span className="col-tag font-mono">WITH MUSE: TOPOLOGICALLY CLUSTERED</span>
              </div>
              <div className="clustered-card-inner">
                <div className="cluster-card-head">
                  <span className="badge-clustered font-mono">[CLUSTERED ALERT]</span>
                  <span className="timestamp font-mono">03:14:02</span>
                </div>
                <h4>Shared Upstream Dependency Failure</h4>
                <div className="cluster-meta">
                  <div className="meta-row">
                    <span className="meta-label">Root Cause:</span>
                    <span className="meta-val font-mono">OLT-NORTH-02 / Card 2 / Slot 4</span>
                  </div>
                  <div className="meta-row">
                    <span className="meta-label">Impacted Scope:</span>
                    <span className="meta-val">42 Downstream Subscriber Terminal Devices</span>
                  </div>
                  <div className="meta-row summary-row">
                    <span className="meta-label">Summary:</span>
                    <span className="meta-val quote">"These customer failures appear to share the same upstream feeder card."</span>
                  </div>
                  <div className="meta-row status-suppressed">
                    <span className="status-dot green" />
                    <span>41 downstream alerts SUPPRESSED from paging system</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="caption-disclaimer font-mono text-center">
            *Illustrative example showing how MUSE Telemetry correlates raw telemetry. In production, downstream noise is automatically suppressed while NOC teams receive the single root-cause event.
          </div>

          {/* EXPLANATION GRID */}
          <div className="grid-3 mt-4">
            <div className="flat-card mechanic-card">
              <div className="card-header-dot">
                <Layers size={18} className="icon-blue" />
                <span className="card-tag">TOPOLOGY CORRELATION</span>
              </div>
              <h3>Card, Slot &amp; Splitter Mapping</h3>
              <p>Simultaneous failures on shared cards, splitters, or upstream links are consolidated into one high-priority root-cause alert.</p>
            </div>

            <div className="flat-card mechanic-card">
              <div className="card-header-dot">
                <Bell size={18} className="icon-blue" />
                <span className="card-tag">NOISE SUPPRESSION</span>
              </div>
              <h3>Downstream Alert Suppression</h3>
              <p>Dozens of secondary customer alarms are grouped under the primary incident rather than paging your duty engineer eighty times.</p>
            </div>

            <div className="flat-card mechanic-card">
              <div className="card-header-dot">
                <CheckCircle2 size={18} className="icon-green" />
                <span className="card-tag">TRUCK ROLL EFFICIENCY</span>
              </div>
              <h3>Send One Truck to the Cause</h3>
              <p>Dispatch your technician directly to the actual feeder card or node failure on the first call, instead of chasing downstream tickets.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. "WHAT CHANGED LAST NIGHT?" (CONFIG CHANGE DETECTION) */}
      <section className="section-padding border-top">
        <div className="section-container">
          <div className="grid-2 align-center">
            <div>
              <div className="card-header-dot">
                <GitCommit size={18} className="icon-blue" />
                <span className="card-tag">CONFIGURATION CHANGE DETECTION</span>
              </div>
              <h2>"What Changed Last Night?"</h2>
              <p className="lead-text">
                The worst morning outage is the one where hardware is up, but traffic is blackholing, and nobody remembers who touched what after hours.
              </p>
              <p>
                MUSE Telemetry continuously collects and compares device configurations over time. When an outage occurs, it immediately flags configuration changes that occurred prior to the event.
              </p>

              <ul className="bullet-list-check mt-3">
                <li>
                  <CheckCircle2 size={16} className="text-green" />
                  <span><strong>Automated Diff Tracking:</strong> View exact side-by-side line changes in device configs across vendors.</span>
                </li>
                <li>
                  <CheckCircle2 size={16} className="text-green" />
                  <span><strong>Timeline Correlation:</strong> See instantly if a late-night VLAN edit or firmware push correlates with subscriber drops.</span>
                </li>
                <li>
                  <CheckCircle2 size={16} className="text-green" />
                  <span><strong>Fast Path to Resolution:</strong> Move from <em>"everything is on fire"</em> to <em>"here is the line that broke it"</em> in seconds.</span>
                </li>
              </ul>
            </div>

            <div className="flat-card config-diff-mock">
              <div className="mock-head">
                <span className="font-mono text-dim">CONFIG DIFF: OLT-NORTH-02.cfg</span>
                <span className="status-dot amber" />
              </div>
              <div className="diff-body font-mono">
                <div className="diff-line diff-same"> interface gpon-olt_1/2/4</div>
                <div className="diff-line diff-del">-  vlan-profile 202_RESIDENTIAL_VOIP default</div>
                <div className="diff-line diff-add">+  vlan-profile 909_UNTAGGED_TESTing</div>
                <div className="diff-line diff-same">  description "Feeder Splitter 04 - Sector B"</div>
                <div className="diff-line diff-same">  no shutdown</div>
              </div>
              <div className="diff-footer font-mono">
                <span>Modified 02:47 AM by net_admin (correlates with 03:14 AM outage)</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. CUSTOMER-LEVEL VISIBILITY */}
      <section className="section-padding border-top">
        <div className="section-container">
          <div className="section-heading text-center">
            <h2>Know Customer Impact Before They Call Support</h2>
            <p>Combine outside-in subscriber experience with inside-out network health.</p>
          </div>

          <div className="grid-2">
            <div className="flat-card">
              <div className="card-header-dot">
                <Users size={18} className="icon-blue" />
                <span className="card-tag">CUSTOMER INCIDENT HISTORY</span>
              </div>
              <h3>Pattern &amp; Frequency Detection</h3>
              <p>
                When a subscriber calls support complaining about <em>"recurring drops,"</em> your NOC can instantly view performance history to see if this is an isolated event or the <strong>"fourth outage detected in this area this month."</strong>
              </p>
            </div>

            <div className="flat-card">
              <div className="card-header-dot">
                <Server size={18} className="icon-blue" />
                <span className="card-tag">OUTSIDE-IN + INSIDE-OUT</span>
              </div>
              <h3>Geographic &amp; Dependency Mapping</h3>
              <p>
                Identify micro-outages affecting specific streets or splitters before churn spikes. Connect customer experience metrics directly to core device, card, and link health.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. FITS YOUR EXISTING TOOLS */}
      <section className="section-padding border-top">
        <div className="section-container">
          <div className="section-heading text-center">
            <h2>Alerts Flow to Where Your Team Already Lives</h2>
            <p>Zero forced workflow changes. Designed for NOC engineers and Field Techs.</p>
          </div>

          <div className="grid-3">
            <div className="flat-card">
              <div className="card-header-dot">
                <Bell size={18} className="icon-blue" />
                <span className="card-tag">INTEGRATIONS</span>
              </div>
              <h3>Paging &amp; Destinations</h3>
              <p>Outbound alerts integrate seamlessly with PagerDuty, Opsgenie, webhooks, SMS, and email notifications.</p>
            </div>

            <div className="flat-card">
              <div className="card-header-dot">
                <Server size={18} className="icon-blue" />
                <span className="card-tag">NOC PORTAL</span>
              </div>
              <h3>Multi-Monitor Command</h3>
              <p>High-density, real-time operational dashboard built for NOC teams managing complex outages.</p>
            </div>

            <div className="flat-card">
              <div className="card-header-dot">
                <Smartphone size={18} className="icon-blue" />
                <span className="card-tag">FIELD TECH VIEW</span>
              </div>
              <h3>Mobile-Optimized Interface</h3>
              <p>Lightweight, fast-loading mobile view tailored for technicians on-site in the bucket truck or central office.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 7. PROOF + RISK REVERSAL */}
      <section className="section-padding border-top">
        <div className="section-container">
          <div className="flat-card offer-highlight-card">
            <div className="section-heading text-center mb-4">
              <h2>Evaluate Event Clustering on Your Production Network</h2>
              <p>Verify root-cause correlation and downstream noise suppression on your live traffic.</p>
            </div>

            <div className="offer-points-grid">
              <div className="offer-point font-body">
                <CheckCircle2 size={20} className="check-icon" />
                <div>
                  <strong>Live Production Pilot</strong>
                  <p>Deploy the collector appliance behind your firewall on a segment of your network to test against real-world incidents.</p>
                </div>
              </div>

              <div className="offer-point font-body">
                <CheckCircle2 size={20} className="check-icon" />
                <div>
                  <strong>Founder-Led Deployment</strong>
                  <p>Work directly with the engineer who built the platform to stand up the collector, configure pollers, and tune alert thresholds.</p>
                </div>
              </div>

              <div className="offer-point font-body">
                <CheckCircle2 size={20} className="check-icon" />
                <div>
                  <strong>Interactive Forge Sandbox</strong>
                  <p>Explore device schema proposals and parser rules in our in-browser demo playground before connecting new hardware.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. CLOSE: FINAL CTA */}
      <section className="section-padding border-top pb-6">
        <div className="section-container">
          <div className="flat-card cta-card-center ops-closing-card">
            <h2>Bring Your Last Big Outage to a 15-Minute Call</h2>
            <p className="cta-lead">
              We'll take your last major incident (including your hardware mix, alert volume, and timeline) and show you step-by-step what it would have looked like on one screen in MUSE Telemetry.
            </p>

            <div className="cta-actions flex-col-center">
              <button onClick={onOpenLeadModal} className="btn btn-primary btn-hero-lg">
                <span>Book Your 15-Minute Outage Review</span>
                <ArrowRight size={16} />
              </button>
              <p className="cta-microcopy mt-3">
                15 minutes: bring your last big outage and see what it looks like on one screen. Practical conversation with an experienced network engineer.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* STYLES FOR OPS LANDING PAGE */}
      <style>{`
        .ops-landing-page {
          width: 100%;
        }

        .landing-hero-section {
          padding: 5.5rem 0 3.5rem;
          text-align: center;
        }

        .hero-content-center {
          max-width: 860px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .hero-title {
          font-size: 3.2rem;
          font-weight: 800;
          color: #ffffff;
          line-height: 1.15;
          letter-spacing: -0.035em;
          margin-bottom: 1.25rem;
        }

        .hero-subtitle {
          font-size: 1.15rem;
          color: #999999;
          line-height: 1.6;
          margin-bottom: 2rem;
          max-width: 760px;
        }

        .cta-wrapper {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1.75rem;
        }

        .btn-hero-lg {
          padding: 0.85rem 1.85rem;
          font-size: 1rem;
          font-weight: 600;
          border-radius: var(--radius-sm);
        }

        .cta-microcopy {
          font-size: 0.82rem;
          color: var(--text-muted);
          max-width: 540px;
          line-height: 1.45;
          text-align: center;
        }

        .credibility-microline {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          font-size: 0.8rem;
          color: var(--text-dim);
          max-width: 680px;
          margin-bottom: 2rem;
          line-height: 1.4;
        }

        .text-muted-icon {
          color: var(--text-dim);
          flex-shrink: 0;
        }

        .anchor-subtle {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          color: var(--text-muted);
          font-size: 0.84rem;
          text-decoration: none;
          transition: color 0.15s ease;
        }

        .anchor-subtle:hover {
          color: #ffffff;
        }

        /* TRUST STRIP */
        .trust-strip-section {
          padding: 0.5rem 0 2.5rem;
        }

        .trust-banner-card {
          background: #06090e;
          border: 1px solid #1e293b;
          border-radius: var(--radius-md);
          padding: 1.35rem 1.75rem;
          display: flex;
          align-items: flex-start;
          gap: 1.25rem;
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
        }

        .green-badge {
          background: rgba(16, 185, 129, 0.12) !important;
          color: #10b981 !important;
          border-color: rgba(16, 185, 129, 0.25) !important;
        }

        .trust-icon-badge {
          padding: 0.65rem;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          border: 1px solid;
        }

        .trust-banner-body h3 {
          font-size: 0.98rem;
          font-weight: 600;
          color: #ffffff;
          margin-bottom: 0.35rem;
        }

        .trust-banner-body p {
          font-size: 0.85rem;
          color: #94a3b8;
          line-height: 1.5;
          margin: 0;
        }

        /* VILLAIN STORY CARD */
        .villain-story-card {
          background: #090505;
          border: 1px solid #2d1414;
          padding: 2.75rem 2.25rem;
        }

        .villain-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.75rem;
          font-weight: 600;
          color: #ef4444;
          letter-spacing: 0.05em;
          margin-bottom: 1rem;
        }

        .text-red { color: #ef4444; }
        .text-green { color: #10b981; }

        .villain-story-card h2 {
          font-size: 1.85rem;
          margin-bottom: 1.25rem;
        }

        .story-body {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          font-size: 0.95rem;
          line-height: 1.7;
          color: var(--text-muted);
        }

        .highlight-question {
          font-size: 1.1rem;
          font-weight: 600;
          color: #ffffff;
          border-left: 2px solid #ef4444;
          padding-left: 1rem;
          margin: 0.5rem 0;
        }

        /* CLUSTERING SPLIT GRID */
        .clustering-split-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
          margin-top: 2rem;
        }

        .cluster-column {
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
          padding: 1.5rem;
          display: flex;
          flex-direction: column;
        }

        .noise-col {
          background: #050303;
          border-color: #261212;
        }

        .rootcause-col {
          background: #030805;
          border-color: #122b1c;
        }

        .col-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .col-tag {
          font-size: 0.72rem;
          letter-spacing: 0.04em;
          color: var(--text-dim);
        }

        .terminal-box {
          background: #000000;
          border: 1px solid #1a1a1a;
          border-radius: var(--radius-sm);
          padding: 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.45rem;
          font-family: var(--font-mono);
          font-size: 0.75rem;
          flex-grow: 1;
        }

        .term-line { line-height: 1.4; }
        .red-txt { color: #f87171; }
        .amber-txt { color: #fbbf24; }
        .dim-txt { color: #555555; }

        .clustered-card-inner {
          background: #000000;
          border: 1px solid #10b981;
          border-radius: var(--radius-sm);
          padding: 1.25rem;
          display: flex;
          flex-direction: column;
          gap: 0.85rem;
          box-shadow: 0 0 20px rgba(16, 185, 129, 0.1);
          flex-grow: 1;
        }

        .cluster-card-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .badge-clustered {
          background: rgba(16, 185, 129, 0.15);
          color: #10b981;
          font-size: 0.7rem;
          font-weight: 700;
          padding: 0.2rem 0.5rem;
          border-radius: 4px;
          border: 1px solid rgba(16, 185, 129, 0.3);
        }

        .timestamp {
          font-size: 0.75rem;
          color: var(--text-dim);
        }

        .clustered-card-inner h4 {
          font-size: 1.1rem;
          color: #ffffff;
        }

        .cluster-meta {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          font-size: 0.84rem;
        }

        .meta-row {
          display: flex;
          gap: 0.5rem;
        }

        .meta-label {
          color: var(--text-dim);
          min-width: 105px;
        }

        .meta-val { color: var(--text-primary); }
        .quote { font-style: italic; color: #a7f3d0; }

        .status-suppressed {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.8rem;
          color: #10b981;
          background: rgba(16, 185, 129, 0.08);
          padding: 0.4rem 0.65rem;
          border-radius: 4px;
          margin-top: 0.5rem;
        }

        .caption-disclaimer {
          font-size: 0.72rem;
          color: var(--text-dim);
          margin-top: 0.85rem;
          line-height: 1.4;
        }

        .icon-blue { color: #3b82f6; }
        .icon-green { color: #10b981; }

        /* CONFIG DIFF MOCK */
        .config-diff-mock {
          background: #050505;
          border: 1px solid var(--border-color);
          padding: 1.5rem;
        }

        .mock-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 0.75rem;
          border-bottom: 1px solid var(--border-color);
          padding-bottom: 0.75rem;
          margin-bottom: 1rem;
        }

        .diff-body {
          font-size: 0.78rem;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .diff-line {
          padding: 0.2rem 0.4rem;
          border-radius: 3px;
        }

        .diff-same { color: var(--text-muted); }
        .diff-del { background: rgba(239, 68, 68, 0.15); color: #f87171; }
        .diff-add { background: rgba(16, 185, 129, 0.15); color: #34d399; }

        .diff-footer {
          margin-top: 1rem;
          font-size: 0.72rem;
          color: var(--text-dim);
          border-top: 1px dashed var(--border-color);
          padding-top: 0.75rem;
        }

        .bullet-list-check {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          font-size: 0.9rem;
        }

        .bullet-list-check li {
          display: flex;
          align-items: flex-start;
          gap: 0.65rem;
          line-height: 1.5;
        }

        .bullet-list-check li span {
          color: var(--text-muted);
        }

        .bullet-list-check li strong {
          color: #ffffff;
        }

        .align-center {
          align-items: center;
        }

        .lead-text {
          font-size: 1.05rem;
          color: var(--text-primary);
          line-height: 1.6;
          margin-bottom: 0.75rem;
        }

        /* OFFER & CLOSING */
        .offer-highlight-card {
          background: linear-gradient(180deg, #0a0a0a 0%, #05080c 100%);
          border: 1px solid #1e293b;
          padding: 3rem 2.5rem;
        }

        .offer-points-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2rem;
          margin-top: 1rem;
        }

        .offer-point {
          display: flex;
          gap: 0.85rem;
          align-items: flex-start;
        }

        .check-icon {
          color: var(--dot-green);
          flex-shrink: 0;
          margin-top: 0.15rem;
        }

        .offer-point strong {
          display: block;
          color: #ffffff;
          font-size: 0.98rem;
          margin-bottom: 0.35rem;
        }

        .offer-point p {
          font-size: 0.85rem;
          line-height: 1.5;
          margin: 0;
        }

        .ops-closing-card {
          padding: 3.5rem 2rem;
        }

        .flex-col-center {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .mb-2 { margin-bottom: 0.5rem; }
        .mb-3 { margin-bottom: 0.75rem; }
        .mb-4 { margin-bottom: 1.5rem; }
        .mt-3 { margin-top: 0.75rem; }
        .mt-4 { margin-top: 1.5rem; }

        @media (max-width: 950px) {
          .clustering-split-grid, .offer-points-grid {
            grid-template-columns: 1fr;
          }
          .hero-title { font-size: 2.3rem; }
          .villain-story-card h2 { font-size: 1.5rem; }
        }
      `}</style>
    </div>
  );
};

export default OpsLandingPage;
